
import { useState, useRef, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { Variable, X } from 'lucide-react';
import type { NodeViewProps } from '@tiptap/react';

/**
 * VariableNodeView - React component for rendering variable chips in TipTap
 * 
 * Features:
 * - Icon + formatted variable name
 * - Delete button (x) when not readonly
 * - Click to edit name inline
 * - Sample data display in readonly mode
 */
export function VariableNodeView({ node, updateAttributes, deleteNode, extension }: NodeViewProps) {
    const { name } = node.attrs;
    const { sampleData, readOnly, onDelete, onRename } = extension.options;
    
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(name);
    const inputRef = useRef<HTMLInputElement>(null);

    // Format variable name for display (snake_case → Title Case)
    const formatName = (varName: string): string => {
        return varName
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleClick = (e: React.MouseEvent) => {
        if (readOnly) return;
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        setEditValue(name);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
        }
    };

    const commitEdit = () => {
        const trimmed = editValue.trim().replace(/\s+/g, '_').toLowerCase();
        if (trimmed && trimmed !== name) {
            updateAttributes({ name: trimmed });
            onRename?.(name, trimmed);
        }
        setIsEditing(false);
    };

    const cancelEdit = () => {
        setEditValue(name);
        setIsEditing(false);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDelete?.(name);
        deleteNode();
    };

    // Display text: sample data in readonly mode, formatted name otherwise
    const displayText = readOnly && sampleData?.[name] 
        ? sampleData[name] 
        : formatName(name);

    return (
        <NodeViewWrapper as="span" className="inline">
            <span
                className={`
                    inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5
                    rounded-md text-xs font-medium align-baseline
                    bg-amber-100 text-amber-800 border border-amber-200
                    ${!readOnly ? 'cursor-pointer hover:bg-amber-200 hover:border-amber-300' : ''}
                    transition-colors duration-150
                `}
                onClick={handleClick}
                title={readOnly && sampleData?.[name] ? `{{${name}}}` : undefined}
            >
                <Variable size={12} className="text-amber-600 flex-shrink-0" />
                
                {isEditing ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={commitEdit}
                        className="
                            w-20 px-1 py-0 text-xs font-medium
                            bg-white border border-amber-300 rounded
                            text-amber-900 outline-none
                            focus:ring-1 focus:ring-amber-400
                        "
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <span className="max-w-32 truncate">{displayText}</span>
                )}

                {!readOnly && !isEditing && (
                    <button
                        type="button"
                        onClick={handleDelete}
                        className="
                            ml-0.5 p-0.5 rounded
                            text-amber-500 hover:text-amber-700
                            hover:bg-amber-200
                            transition-colors duration-150
                        "
                        title="Remove variable"
                    >
                        <X size={10} />
                    </button>
                )}
            </span>
        </NodeViewWrapper>
    );
}

